/**
 * AgentFlowBuilder — Visual node-based agent workflow builder
 *
 * Uses @xyflow/react (React Flow v12) for interactive canvas.
 * Users drag nodes from the palette, connect them with edges,
 * and save/load blueprints via the agent factory API.
 *
 * Inspired by GCP Vertex AI Agent Designer.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Save, FolderOpen, Trash2, Play, Loader2, Plus,
  ChevronDown, X, Check,
} from 'lucide-react';

import { nodeTypes, DEFAULT_NODES, DEFAULT_EDGES } from './flow/FlowNodes';
import NodePalette from './flow/NodePalette';
import NodeConfigPanel from './flow/NodeConfigPanel';
import useAgentFactory from '../../hooks/useAgentFactory';
import { AGENT_TOOLS, DATA_SOURCES } from './canvasConfig';

// Edge defaults
const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: '#E8B80060', strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#E8B80060', width: 16, height: 16 },
};

// Collision-safe node ID generation
function getNextNodeId(type) {
  return `${type}-${crypto.randomUUID().slice(0, 8)}`;
}

export default function AgentFlowBuilder({ sport = 'basketball', initialConfig = null }) {
  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState(DEFAULT_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(DEFAULT_EDGES);
  const [hasManualEdits, setHasManualEdits] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  // Agent factory integration
  const {
    agents, isCreating, isLoadingAgents, error,
    listAgents, createAgent, deleteAgent, executeAgent, listTools,
  } = useAgentFactory();

  // UI state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [executionOutput, setExecutionOutput] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Load saved agents on mount
  useEffect(() => {
    listAgents('', '', sport);
  }, [listAgents, sport]);

  // Auto-populate flow from Configure tab's agentConfig (only if canvas is untouched)
  useEffect(() => {
    if (!initialConfig || hasManualEdits) return;
    const hasSources = initialConfig.sources?.length > 0;
    const hasTools = initialConfig.tools?.length > 0;
    if (!hasSources && !hasTools) return;

    // Check if flow is still in default state
    const isDefault = nodes.length === DEFAULT_NODES.length &&
      nodes.every((n, i) => n.id === DEFAULT_NODES[i]?.id);
    if (!isDefault) return;

    const newNodes = [
      { id: 'start-1', type: 'start', position: { x: 50, y: 200 }, data: { label: 'Start', triggerType: 'Manual trigger' } },
      { id: 'agent-main', type: 'agent', position: { x: 350, y: 150 }, data: { label: initialConfig.name || 'My Agent', description: initialConfig.description || '', model: 'Gemini 3 Flash' } },
    ];
    const newEdges = [
      { id: 'e-start-agent', source: 'start-1', target: 'agent-main', ...defaultEdgeOptions },
    ];

    // Add source nodes on the left
    (initialConfig.sources || []).forEach((sourceId, i) => {
      const source = DATA_SOURCES.find(s => s.id === sourceId);
      const id = `source-cfg-${i}`;
      newNodes.push({
        id, type: 'dataSource',
        position: { x: 50, y: 50 + i * 90 },
        data: { label: source?.name || sourceId, toolId: sourceId, description: source?.desc || '' },
      });
      newEdges.push({ id: `e-${id}-agent`, source: id, target: 'agent-main', ...defaultEdgeOptions });
    });

    // Add tool nodes on the right
    (initialConfig.tools || []).forEach((toolId, i) => {
      const tool = AGENT_TOOLS.find(t => t.id === toolId);
      const id = `tool-cfg-${i}`;
      newNodes.push({
        id, type: 'tool',
        position: { x: 650, y: 50 + i * 90 },
        data: { label: tool?.name || toolId, toolId, description: tool?.desc || '' },
      });
      newEdges.push({ id: `e-agent-${id}`, source: 'agent-main', target: id, ...defaultEdgeOptions });
    });

    setNodes(newNodes);
    setEdges(newEdges);
  }, [initialConfig, hasManualEdits, nodes, setNodes, setEdges]);

  // Show notification helper
  const notify = useCallback((msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // ─── Connection Handler ────────────────────────────────────────────────────
  const onConnect = useCallback((params) => {
    setEdges(eds => addEdge({ ...params, ...defaultEdgeOptions }, eds));
  }, [setEdges]);

  // ─── Node Selection ────────────────────────────────────────────────────────
  const onNodeClick = useCallback((_event, node) => {
    setSelectedNode(node);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  // ─── Update Node Data ──────────────────────────────────────────────────────
  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes(nds =>
      nds.map(n => n.id === nodeId ? { ...n, data: newData } : n)
    );
    // Also update selectedNode reference
    setSelectedNode(prev =>
      prev && prev.id === nodeId ? { ...prev, data: newData } : prev
    );
  }, [setNodes]);

  // ─── Delete Node ───────────────────────────────────────────────────────────
  const deleteNode = useCallback((nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
  }, [setNodes, setEdges]);

  // ─── Drag & Drop from Palette ──────────────────────────────────────────────
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    setIsDragOver(false);

    const type = event.dataTransfer.getData('application/reactflow-type');
    const dataStr = event.dataTransfer.getData('application/reactflow-data');

    if (!type || !reactFlowInstance) return;

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    let data = { label: type };
    try {
      data = JSON.parse(dataStr);
    } catch { /* use default */ }

    const newNode = {
      id: getNextNodeId(type),
      type,
      position,
      data,
    };

    setNodes(nds => [...nds, newNode]);
    setHasManualEdits(true);
  }, [reactFlowInstance, setNodes]);

  // ─── Serialize Flow to Blueprint ───────────────────────────────────────────
  const serializeToBlueprint = useCallback(() => {
    const agentNodes = nodes.filter(n => n.type === 'agent');
    const toolNodes = nodes.filter(n => n.type === 'tool');
    const sourceNodes = nodes.filter(n => n.type === 'dataSource');
    const outputNodes = nodes.filter(n => n.type === 'output');

    return {
      name: saveName || (agentNodes[0]?.data?.label || 'My Agent'),
      description: saveDescription || (agentNodes[0]?.data?.description || ''),
      sport,
      tools: toolNodes.map(n => n.data.toolId).filter(Boolean),
      sources: sourceNodes.map(n => n.data.toolId || n.data.label),
      outputs: outputNodes.map(n => n.data.format || n.data.label || 'chat'),
      // Store flow graph for future reload
      flow: { nodes, edges },
    };
  }, [nodes, edges, saveName, saveDescription, sport]);

  // ─── Save Blueprint ────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const blueprint = serializeToBlueprint();
    const result = await createAgent(
      `${blueprint.name}: ${blueprint.description}`,
      sport,
    );
    if (result) {
      notify(`Saved "${result.name}"`);
      setShowSaveDialog(false);
      setSaveName('');
      setSaveDescription('');
    }
  }, [serializeToBlueprint, createAgent, sport, notify]);

  // ─── Load Blueprint ────────────────────────────────────────────────────────
  const handleLoad = useCallback((agent) => {
    // If the agent has stored flow data, restore it
    if (agent.flow?.nodes && agent.flow?.edges) {
      setNodes(agent.flow.nodes);
      setEdges(agent.flow.edges);
    } else {
      // Reconstruct basic flow from blueprint data
      const newNodes = [
        { id: 'start-1', type: 'start', position: { x: 50, y: 200 }, data: { label: 'Start', triggerType: 'Manual trigger' } },
        { id: 'agent-loaded', type: 'agent', position: { x: 350, y: 150 }, data: { label: agent.name, description: agent.description, model: 'Gemini 3 Flash' } },
      ];
      const newEdges = [
        { id: 'e-start-agent', source: 'start-1', target: 'agent-loaded', ...defaultEdgeOptions },
      ];

      // Add tool nodes
      (agent.tools || []).forEach((toolId, i) => {
        const id = `tool-loaded-${i}`;
        newNodes.push({
          id, type: 'tool',
          position: { x: 650, y: 80 + i * 100 },
          data: { label: toolId, toolId, description: '' },
        });
        newEdges.push({ id: `e-agent-${id}`, source: 'agent-loaded', target: id, ...defaultEdgeOptions });
      });

      setNodes(newNodes);
      setEdges(newEdges);
    }

    setShowLoadDialog(false);
    notify(`Loaded "${agent.name}"`);
  }, [setNodes, setEdges, notify]);

  // ─── Delete Blueprint ──────────────────────────────────────────────────────
  const handleDeleteBlueprint = useCallback(async (agentId) => {
    const ok = await deleteAgent(agentId);
    if (ok) notify('Blueprint deleted');
  }, [deleteAgent, notify]);

  // ─── Execute Agent ─────────────────────────────────────────────────────────
  const handleExecute = useCallback(async () => {
    const agentNode = nodes.find(n => n.type === 'agent');
    if (!agentNode) {
      notify('Add an Agent node first', 'error');
      return;
    }

    setIsExecuting(true);
    setExecutionOutput('');

    // Find a matching saved agent or create one on the fly
    const matchingAgent = agents.find(a =>
      a.name === agentNode.data.label || a.description?.includes(agentNode.data.label)
    );

    if (matchingAgent) {
      await executeAgent(matchingAgent.agent_id, 'Introduce yourself and describe what you can do.', {
        onText: (chunk, full) => setExecutionOutput(full),
        onDone: () => setIsExecuting(false),
        onError: (err) => {
          notify(err, 'error');
          setIsExecuting(false);
        },
      });
    } else {
      // Create agent first, then execute
      const bp = await createAgent(
        `${agentNode.data.label}: ${agentNode.data.description || 'General purpose agent'}`,
        sport,
      );
      if (bp) {
        await executeAgent(bp.agent_id, 'Introduce yourself and describe what you can do.', {
          onText: (chunk, full) => setExecutionOutput(full),
          onDone: () => setIsExecuting(false),
          onError: (err) => {
            notify(err, 'error');
            setIsExecuting(false);
          },
        });
      } else {
        setIsExecuting(false);
      }
    }
  }, [nodes, agents, executeAgent, createAgent, sport, notify]);

  // ─── Clear Canvas ──────────────────────────────────────────────────────────
  const handleClear = useCallback(() => {
    setNodes(DEFAULT_NODES);
    setEdges(DEFAULT_EDGES);
    setSelectedNode(null);
    setExecutionOutput('');
    setHasManualEdits(false);
  }, [setNodes, setEdges]);

  return (
    <div className="flex h-[calc(100vh-120px)] gap-0">
      {/* Left: Node Palette */}
      <NodePalette onDragStart={() => {}} />

      {/* Center: React Flow Canvas */}
      <div className="flex-1 relative" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          proOptions={{ hideAttribution: true }}
          style={{
            background: '#000',
            borderRadius: '12px',
            border: isDragOver ? '2px dashed #E8B80060' : '1px solid #1A1A1A',
            margin: '0 4px',
          }}
        >
          <Background color="#1A1A1A" gap={20} size={1} />
          <Controls
            position="bottom-left"
            style={{ background: '#111', border: '1px solid #1A1A1A', borderRadius: 8 }}
          />
          <MiniMap
            position="bottom-right"
            nodeColor={(n) => {
              const colors = { start: '#00CC66', agent: '#E8B800', tool: '#0088FF', dataSource: '#00ff88', output: '#CC66FF', condition: '#FF9500' };
              return colors[n.type] || '#555';
            }}
            maskColor="rgba(0,0,0,0.8)"
            style={{ background: '#0A0A0A', border: '1px solid #1A1A1A', borderRadius: 8 }}
          />

          {/* Top toolbar */}
          <Panel position="top-left" className="flex items-center gap-2">
            <ToolbarButton icon={Save} label="Save" onClick={() => setShowSaveDialog(true)} />
            <ToolbarButton icon={FolderOpen} label="Load" onClick={() => { setShowLoadDialog(true); listAgents('', '', sport); }} />
            <ToolbarButton icon={Trash2} label="Clear" onClick={handleClear} />
            <div className="w-px h-6 mx-1" style={{ background: '#1A1A1A' }} />
            <ToolbarButton
              icon={isExecuting ? Loader2 : Play}
              label={isExecuting ? 'Running...' : 'Run'}
              onClick={handleExecute}
              primary
              disabled={isExecuting}
              spinning={isExecuting}
            />
          </Panel>

          {/* Notification */}
          {notification && (
            <Panel position="top-center">
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium animate-pulse"
                style={{
                  background: notification.type === 'error' ? '#FF444420' : '#00CC6620',
                  border: `1px solid ${notification.type === 'error' ? '#FF444440' : '#00CC6640'}`,
                  color: notification.type === 'error' ? '#FF4444' : '#00CC66',
                }}
              >
                {notification.type === 'error' ? <X size={14} /> : <Check size={14} />}
                {notification.msg}
              </div>
            </Panel>
          )}

          {/* Drop zone indicator */}
          {isDragOver && (
            <Panel position="top-center" className="pointer-events-none">
              <div className="px-4 py-2 rounded-xl text-xs font-medium" style={{ background: '#E8B80015', border: '1px dashed #E8B80040', color: '#E8B800' }}>
                Drop to add node
              </div>
            </Panel>
          )}
        </ReactFlow>

        {/* Execution output overlay */}
        {executionOutput && (
          <div
            className="absolute bottom-4 left-4 right-4 max-h-[200px] overflow-auto rounded-xl p-4"
            style={{ background: '#0A0A0AE0', border: '1px solid #1A1A1A', backdropFilter: 'blur(8px)' }}
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#E8B800' }}>
                Agent Output
              </p>
              <button onClick={() => setExecutionOutput('')} className="p-1 rounded hover:bg-white/[0.04]">
                <X size={12} style={{ color: '#555' }} />
              </button>
            </div>
            <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: '#F5F5F5' }}>
              {executionOutput}
            </p>
          </div>
        )}

        {/* Save Dialog */}
        {showSaveDialog && (
          <DialogOverlay onClose={() => setShowSaveDialog(false)}>
            <h3 className="text-sm font-medium mb-4" style={{ color: '#F5F5F5' }}>Save Agent Blueprint</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#555' }}>Name</label>
                <input
                  type="text"
                  value={saveName}
                  onChange={e => setSaveName(e.target.value)}
                  placeholder="e.g. Basketball Scout Agent"
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none"
                  style={{ background: '#0A0A0A', border: '1px solid #1A1A1A', color: '#F5F5F5' }}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#555' }}>Description</label>
                <textarea
                  value={saveDescription}
                  onChange={e => setSaveDescription(e.target.value)}
                  placeholder="What does this agent do?"
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-xs outline-none resize-none"
                  style={{ background: '#0A0A0A', border: '1px solid #1A1A1A', color: '#F5F5F5' }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveDialog(false)}
                className="px-4 py-2 rounded-lg text-xs"
                style={{ color: '#999' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isCreating}
                className="px-4 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ background: '#E8B800', color: '#000' }}
              >
                {isCreating ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                {isCreating ? 'Saving...' : 'Save'}
              </button>
            </div>
          </DialogOverlay>
        )}

        {/* Load Dialog */}
        {showLoadDialog && (
          <DialogOverlay onClose={() => setShowLoadDialog(false)}>
            <h3 className="text-sm font-medium mb-4" style={{ color: '#F5F5F5' }}>Load Agent Blueprint</h3>
            {isLoadingAgents ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin" style={{ color: '#E8B800' }} />
              </div>
            ) : agents.length === 0 ? (
              <p className="text-center py-8 text-xs" style={{ color: '#555' }}>No saved agents yet</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-auto">
                {agents.map(agent => (
                  <div
                    key={agent.agent_id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all hover:scale-[1.01]"
                    style={{ background: '#0A0A0A', border: '1px solid #1A1A1A' }}
                    onClick={() => handleLoad(agent)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate" style={{ color: '#F5F5F5' }}>{agent.name}</p>
                      <p className="text-[10px] truncate" style={{ color: '#555' }}>{agent.description}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: '#E8B80015', color: '#E8B800' }}>
                        {agent.sport}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteBlueprint(agent.agent_id); }}
                        className="p-1 rounded hover:bg-red-500/10"
                      >
                        <Trash2 size={12} style={{ color: '#FF4444' }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogOverlay>
        )}
      </div>

      {/* Right: Node Config Panel */}
      {selectedNode && (
        <NodeConfigPanel
          node={selectedNode}
          onUpdate={updateNodeData}
          onDelete={deleteNode}
          onClose={() => setSelectedNode(null)}
        />
      )}
    </div>
  );
}

// ─── Toolbar Button ──────────────────────────────────────────────────────────
function ToolbarButton({ icon: Icon, label, onClick, primary, disabled, spinning }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all disabled:opacity-50"
      style={{
        background: primary ? '#E8B800' : '#111',
        color: primary ? '#000' : '#999',
        border: `1px solid ${primary ? '#E8B800' : '#1A1A1A'}`,
      }}
    >
      <Icon size={13} className={spinning ? 'animate-spin' : ''} />
      {label}
    </button>
  );
}

// ─── Dialog Overlay ──────────────────────────────────────────────────────────
function DialogOverlay({ children, onClose }) {
  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div
        className="w-[400px] rounded-2xl p-5"
        style={{ background: '#111', border: '1px solid #1A1A1A' }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
